import { DEFAULT_DATE_FORMAT } from 'module/types/common';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { HeadingCompact01 } from 'module/ui/components/styles';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { Skeleton } from 'antd';
import { AccountPeopleProfile } from 'api/account-people/profile-type';
import { SignalType } from 'api/signal/types';
import { Moment } from 'moment';
import { useGetSignalOccurrencesGroupedForUserPaginatedQuery } from 'queries/account-people';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SignalOccurrence } from './signal-occurrence';
import { Sections } from '../../content-navigator';
import { EmptyStateForSection } from '../../content-section';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
const HeaderIcon = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  ${HeadingCompact01};
  color: var(--text-tertiary);
  position: sticky;
  top: 26px;
  z-index: 99;
  background-color: var(--white-100);
  width: calc(100% + 4px);
  margin-left: -2px;
  margin-right: -2px;
  padding: 0 2px;
  &:after {
    position: absolute;
    content: '';
    width: 100%;
    height: var(--spacing-03);
    bottom: calc(var(--spacing-03) * -1);
    background: var(--white-100);
    z-index: 99;
  }
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-06);
`;

interface Props {
  from: Moment;
  to: Moment;
  userProfile: AccountPeopleProfile;
  signalType: SignalType;
  onDataLoaded?: () => void;
  isFullWidth?: boolean;
}
export const SignalOccurencesForTimePeriod = ({
  from,
  to,
  userProfile,
  signalType,
  onDataLoaded,
  isFullWidth,
}: Props) => {
  const {
    data: signalOccurrences,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetSignalOccurrencesGroupedForUserPaginatedQuery({
    from: from.format(DEFAULT_DATE_FORMAT),
    to: to.format(DEFAULT_DATE_FORMAT),
    signalType,
    userId: userProfile.user.userId,
  });
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isEmpty = (signalOccurrences ?? []).length === 0;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (!hasNextPage || isFetchingNextPage || isLoading) {
            return;
          }
          fetchNextPage();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0 },
    );
    const node = loadMoreRef.current;

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      onDataLoaded?.();
    }
  }, [isLoading, onDataLoaded]);

  return (
    <Wrapper>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Header>
            <HeaderIcon icon={faCalendar} size={12} />
            {humanise(from)} - {humanise(to)}
          </Header>
          {isEmpty ? (
            <EmptyStateForSection
              sectionKey={
                signalType === SignalType.Risk
                  ? Sections.CurrentRisks
                  : Sections.OpportunityLog
              }
              message={
                <FormattedMessage
                  id={
                    signalType === SignalType.Risk
                      ? 'account.people.profile.sections.current_risks.empty'
                      : 'account.people.profile.sections.opportunity_log.empty'
                  }
                />
              }
            />
          ) : (
            <List>
              {signalOccurrences?.map((signalOccurrence) => (
                <SignalOccurrence
                  key={signalOccurrence.firstReportedAt}
                  signalOccurrence={signalOccurrence}
                  signalType={signalType}
                  isFullWidth={isFullWidth}
                />
              ))}
            </List>
          )}
        </>
      )}
      {isLoading || hasNextPage ? (
        <div ref={loadMoreRef} style={{ height: 1 }} />
      ) : null}
    </Wrapper>
  );
};
